// Thin wrapper around posthog-js so callers don't import the library directly
// and we can no-op cleanly when the key isn't configured (local dev / tests).

import posthog from 'posthog-js';
import type { User } from '@punditxi/db';

let initialized = false;

export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com';
  if (!key) return;
  posthog.init(key, {
    api_host: host,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: {
      // Telegram WebView is a constrained surface — autocapture's default
      // selector list can be noisy. Allow it but skip dom_event capture for
      // sensitive nodes (passwords don't exist here, but keeping for safety).
      dom_event_allowlist: ['click', 'change', 'submit'],
    },
    persistence: 'localStorage+cookie',
  });
  initialized = true;
}

export function identify(user: User): void {
  if (!initialized) return;
  posthog.identify(String(user.id), {
    telegram_username: user.telegramUsername ?? undefined,
    has_bracket_premium: user.hasBracketPremium === 1,
    total_predictions: user.predictionsCount,
    total_points: user.totalPoints,
    bracket_points: user.bracketPoints,
    current_streak: user.currentStreak,
    language_code: user.languageCode ?? undefined,
  });
}

export function resetIdentity(): void {
  if (!initialized) return;
  posthog.reset();
}

// Strong-typed event surface. Adding a new event? Extend this map.
export type AnalyticsEvent =
  | { name: 'prediction_submitted'; props: { fixture_id: number; markets_picked: number; boost: boolean; insurance: boolean; is_update: boolean } }
  | { name: 'bracket_entry_paid'; props: { stars_paid: number; was_early_bird: boolean } }
  | { name: 'bracket_locked'; props: { user_id: number } } // server-side fired
  | { name: 'league_created'; props: { league_slug: string; is_public: boolean } }
  | { name: 'league_joined'; props: { league_slug: string; league_type: string } }
  | { name: 'boost_purchased'; props: { fixture_id: number } }
  | { name: 'insurance_purchased'; props: { fixture_id: number } }
  | { name: 'share_clicked'; props: { surface: 'bracket' | 'league' | 'fixture' } }
  | { name: 'subscription_started'; props: { stars_per_period: number } }
  | { name: 'streak_protector_consumed'; props: { streak_length: number } }; // server-side fired

export function track<E extends AnalyticsEvent>(event: E['name'], props: E['props']): void {
  if (!initialized) return;
  posthog.capture(event, props as Record<string, unknown>);
}
